// One returns request, for staff: who, what, the money, the photos, the
// timeline and notes, and the actions that apply to its status.
//
// Actions come from three backend builders (see api/returns.ts). The ADMIN
// ones (approve, decline, check and complete, cancel, note) always exist; the
// LABELS ones (book, add by hand, label PDF, void, mark received) and the
// EXCHANGE ones (release, refund payment, invoice) answer 404 until their
// branches are merged, which shows as "not available yet".
//
// An exchange's order is raised when the exchange is booked, in the order
// status Exchange (BE migration 263), holding its stock; it moves to
// Unfulfilled when the return reaches its milestone, or staff press "Release
// now". The Exchange section shows the new items and what the order waits for.
//
// An auto-approved replacement (worth the instant limit or more, its item
// coming back) is raised at once but held in the order status Exchange until
// the return's first carrier scan; "Send replacement now" releases it early
// (the same release endpoint). The check can charge the return postage when
// the customer's reason wasn't true (postage_charge), with a message saying why.
//
// A request made by staff (New return, entry "staff") says who made it, their
// note, and whether it was made while the customer could still have done it
// themselves; one that replaced open requests links to them, and one that was
// replaced links to the request that replaced it.
import { FormEvent, Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
    X, Check, Ban, ClipboardCheck, Truck, Upload, PackageCheck, FileText, RotateCcw, CreditCard, StickyNote, AlertTriangle, ExternalLink, Send,
} from "lucide-react";
import { useTranslation } from "../../i18n";
import {
    ApiResult, ReplacementHold, ReturnDetail, ReturnExchange, ReturnLine, getReturn, approveReturn, declineReturn, completeReturn, cancelReturn, addReturnNote,
    bookLabel, addManualLabel, voidLabel, markReceived, labelPdfPath, releaseExchange, refundPayment, invoicePdfPath, creditNotePdfPath,
    openProtectedFile, gbp,
} from "../../api/returns";
import { PhotoStrip } from "./ReturnPhotos";
import { ContactList } from "./ReturnContacts";
import { StatusPill, problemLabel, resolutionLabel, eventText, replacementViaText } from "./returnLabels";
import { ALL_ORDER_STATUSES } from "../dispatch/orderStatus";

// An order's status in the Orders page's words ("Exchange", "Unfulfilled", ...); raw when unknown.
const ORDER_STATUSES: ReadonlySet<string> = new Set(ALL_ORDER_STATUSES);
const orderStatusText = (t: (k: string) => string, s: string | null) => (s ? (ORDER_STATUSES.has(s) ? t(`status_${s}`) : s) : "");

interface Props {
    reference: string;
    canWrite: boolean;
    onClose: () => void;
    /** Something changed: the list should reload. */
    onChanged: () => void;
    /** Open another request in this panel (Replaces / Replaced by); without it they link to the Returns page. */
    onOpen?: (reference: string) => void;
}

type Dialog = null | "approve" | "decline" | "complete" | "cancel" | "manual" | "refund_payment" | "release" | "release_replacement";

// The milestones an exchange order can wait for / be released by (released_via adds "staff").
const EXCHANGE_MILESTONES = new Set(["shipped", "delivered", "received", "booked"]);

const orderLink = (orderId: string) => `/admin/warehouse/orders?order=${encodeURIComponent(orderId)}`;

// card_scheme holds the card's scheme for a card ("Visa"), else the method (Checkout.com source).
const PAY_METHODS: Record<string, string> = { applepay: "Apple Pay", googlepay: "Google Pay", paypal: "PayPal", klarna: "Klarna" };

// Carriers as everyone knows them; any other code in capitals.
const CARRIER_NAMES: Record<string, string> = { apc: "APC", inpost: "InPost", royalmail: "Royal Mail", dpd: "DPD", evri: "Evri" };
const carrierName = (code: string | null | undefined) => (code ? CARRIER_NAMES[code.toLowerCase()] ?? code.toUpperCase() : "");

/** A status in words (`${prefix}${status}`), or the raw status when there's no translation for it. */
const statusWord = (t: (k: string) => string, prefix: string, status: string) => {
    const key = `${prefix}${status}`;
    const word = t(key);
    return word === key ? status : word;
};

// What is still the customer's to get back of what they paid on the returns site, in pence: each
// paid payment less its refunds done or on their way (Portal-BE returnRefunds.refundTotals).
const PAID = ["approved", "captured", "refund_pending"];
const REFUND_HELD = ["refunded", "pending", "requested"];
export const leftToRefund = (payments: { status: string; amount_pence: number; refunds?: { status: string; amount_pence: number }[] }[]) => payments
    .filter((p) => PAID.includes(p.status))
    .reduce((sum, p) => sum + Math.max(0, p.amount_pence - (p.refunds ?? []).filter((r) => REFUND_HELD.includes(r.status)).reduce((a, r) => a + r.amount_pence, 0)), 0);

/** "Visa ending 4242", "Apple Pay ending 1234", "PayPal", "Klarna"; "" when nothing is known. */
const paidWith = (p: { card_scheme: string | null; card_last4: string | null }, t: (k: string, v?: Record<string, string>) => string): string => {
    const method = PAY_METHODS[(p.card_scheme ?? "").toLowerCase()];
    if (p.card_last4) return t("rt_card", { scheme: method ?? p.card_scheme ?? "", last4: p.card_last4 }).trim();
    return method ?? "";
};

const ReturnDetailPanel = ({ reference, canWrite, onClose, onChanged, onOpen }: Props) => {
    const { t, language } = useTranslation();
    const [detail, setDetail] = useState<ReturnDetail | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [busy, setBusy] = useState<string | null>(null);
    const [dialog, setDialog] = useState<Dialog>(null);
    const [note, setNote] = useState("");

    const dateTime = useMemo(() => new Intl.DateTimeFormat(language === "pl" ? "pl-PL" : "en-GB", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    }), [language]);
    const dateOnly = useMemo(() => new Intl.DateTimeFormat(language === "pl" ? "pl-PL" : "en-GB", {
        day: "numeric", month: "short", year: "numeric",
    }), [language]);
    const when = (v: string | null | undefined) => (v ? dateTime.format(new Date(v)) : "");
    const day = (v: string | null | undefined) => (v ? dateOnly.format(new Date(v.length === 10 ? `${v}T12:00:00` : v)) : "");

    const load = useCallback(async () => {
        const r = await getReturn(reference);
        if (r.ok && r.data) { setDetail(r.data); setLoadError(null); return; }
        setLoadError(r.status === 404 ? t("rt_not_found") : t("rt_load_failed"));
    }, [reference, t]);

    useEffect(() => { setDetail(null); setLoadError(null); void load(); }, [load]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !dialog) onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [dialog, onClose]);

    // Run an action, explain the outcome, refresh. Returns the result for dialogs that need it.
    const act = async <R,>(key: string, run: () => Promise<ApiResult<R>>, okMessage: string): Promise<ApiResult<R> | null> => {
        setBusy(key);
        try {
            const r = await run();
            if (r.notAvailable) { toast.info(t("rt_not_available")); return r; }
            if (!r.ok) {
                toast.error(r.message || t("rt_action_failed"));
                // stock_short: the release recorded what's missing on the exchange; show it.
                // amount_changed: what's left to refund moved (the Orders page); show the new amount.
                if (r.code === "wrong_status" || r.code === "stock_short" || r.code === "amount_changed") { await load(); onChanged(); }
                return r;
            }
            toast.success((r.data as any)?.already ? t("rt_already_done") : okMessage);
            await load();
            onChanged();
            return r;
        } finally {
            setBusy(null);
        }
    };

    const openFile = async (path: string) => {
        const r = await openProtectedFile(path);
        if (r.notAvailable) toast.info(t("rt_not_available"));
        else if (!r.ok) toast.error(t("rt_file_failed"));
    };

    const submitNote = async (e: FormEvent) => {
        e.preventDefault();
        const text = note.trim();
        if (!text) return;
        const r = await act("note", () => addReturnNote(reference, text), t("rt_note_added"));
        if (r?.ok) setNote("");
    };

    if (!detail) {
        return (
            <Shell onClose={onClose} title={reference}>
                <div className="rt-panel-body"><p className="rt-muted">{loadError ?? t("loading")}</p></div>
            </Shell>
        );
    }

    const d = detail;
    const open = !["completed", "cancelled", "refund_pending", "replacement_sent"].includes(d.status);
    // The customer's own payment on the returns site, and what of it is still theirs to get back.
    const capturedPayment = d.payments.find((p) => p.status === "captured" || p.status === "approved");
    const paymentLeft = leftToRefund(d.payments);
    const canReceive = ["label_pending", "awaiting_return", "in_transit", "exchange_booked"].includes(d.status) && d.flags.needs_return;
    const ex = d.exchange ?? null;
    // Only a held exchange still has an order waiting in Exchange (an older BE sends no exchange block).
    const canRelease = d.resolution === "exchange" && open && (!ex || ex.status === "held");
    const hold = d.replacement_hold ?? null;
    const write = canWrite;

    // How it came in; one made by staff says who, and whether the customer could still have done it themselves.
    const entryText = d.entry === "staff"
        ? (d.created_by?.name ? t("rt_entry_staff_by", { name: d.created_by.name }) : t("rt_entry_staff"))
        : t(d.entry === "link" ? "rt_entry_link" : "rt_entry_direct");
    const madeIn = d.window_at_creation?.open ? d.window_at_creation : null;
    const madeInText = !madeIn ? null
        : typeof madeIn.days_since_delivery === "number" ? t("rt_made_in_window_delivered", { n: String(madeIn.days_since_delivery) })
            : typeof madeIn.days_since_order === "number" ? t("rt_made_in_window_ordered", { n: String(madeIn.days_since_order) })
                : t("rt_made_in_window");
    const replacesRefs = (d.replaces ?? []).filter(Boolean);
    // Another request: opens in this panel (the page's ?ref=), or links to the Returns page.
    const requestLink = (r: string) => (onOpen
        ? <button key={r} type="button" className="rt-ref-link rt-ref-btn" onClick={() => onOpen(r)}>{r}</button>
        : <Link key={r} className="rt-ref-link rt-ref-btn" to={`/admin/returns?ref=${encodeURIComponent(r)}`}>{r}</Link>);

    // The actions for where the request stands, the one to do now first. Cancelling sits apart, and
    // refunding the customer's own payment lives with that payment, never here (Jack, 2026-11-04:
    // it was refunded by mistake, beside "Check and complete", when the return came in).
    const actions: ReactNode[] = [];
    const more: ReactNode[] = [];
    const btn = (key: string, label: string, icon: ReactNode, onClick: () => void, tone: "primary" | "secondary" | "ghost" = "secondary") => (
        <button key={key} type="button" className={`btn btn-sm rt-btn btn-${tone}`} disabled={!write || busy !== null} onClick={onClick}
            title={!write ? t("read_only_mode") : undefined}>
            {icon}{label}
        </button>
    );
    const needsLabel = d.status === "label_pending" || d.actions.book_label === true;
    if (d.actions.approve) actions.push(btn("approve", t("rt_approve"), <Check size={14} />, () => setDialog("approve"), "primary"));
    if (d.actions.decline) actions.push(btn("decline", t("rt_decline"), <Ban size={14} />, () => setDialog("decline")));
    if (needsLabel && d.label_by_hand) {
        // No service was booked for it (a manual return whose label staff add by hand).
        actions.push(btn("manual", t("rt_manual_label"), <Upload size={14} />, () => setDialog("manual"), "primary"));
    } else if (needsLabel) {
        actions.push(btn("book", t("rt_book_label"), <Truck size={14} />, () => act("book", () => bookLabel(reference), t("rt_label_booked")), "primary"));
        actions.push(btn("manual", t("rt_manual_label"), <Upload size={14} />, () => setDialog("manual")));
    }
    // Received: checking it is the next step. Before that, marking it received comes first.
    const completeBtn = d.actions.complete
        ? btn("complete", t("rt_complete"), <ClipboardCheck size={14} />, () => setDialog("complete"), d.status === "received" ? "primary" : "secondary")
        : null;
    if (completeBtn && d.status === "received") actions.push(completeBtn);
    if (canReceive) actions.push(btn("received", t("rt_mark_received"), <PackageCheck size={14} />, () => act("received", () => markReceived(reference), t("rt_marked_received"))));
    if (completeBtn && d.status !== "received") actions.push(completeBtn);
    if (canRelease) actions.push(btn("release", t("rt_release_exchange"), <RotateCcw size={14} />, () => setDialog("release")));
    if (d.actions.release_replacement) actions.push(btn("release_replacement", t("rt_release_replacement"), <Send size={14} />, () => setDialog("release_replacement")));
    if (d.actions.cancel) more.push(btn("cancel", t("rt_cancel"), <X size={14} />, () => setDialog("cancel"), "ghost"));

    const allPhotos = d.photos.map((p) => ({ photo_id: p.photo_id, mime_type: p.mime_type }));
    const photoMeta = (ids: string[]) => ids.map((id) => allPhotos.find((p) => p.photo_id === id) ?? { photo_id: id });
    const q = d.money.quoted;

    // Where an exchange stands, in plain words.
    const exchangeStatus = (x: ReturnExchange): string => {
        if (x.status === "held") {
            return t("rt_exc_held", { when: EXCHANGE_MILESTONES.has(x.release_when) ? t(`rt_exc_when_${x.release_when}`) : x.release_when });
        }
        if (x.status === "released") {
            const via = x.released_via ?? "";
            if (!via) return t("rt_exc_released_plain", { date: when(x.released_at) });
            const how = via === "staff" || EXCHANGE_MILESTONES.has(via) ? t(`rt_exc_via_${via}`) : via;
            return t("rt_exc_released", { date: when(x.released_at), how });
        }
        return x.cancel_reason
            ? t("rt_exc_cancelled", { date: when(x.cancelled_at), why: x.cancel_reason })
            : t("rt_exc_cancelled_plain", { date: when(x.cancelled_at) });
    };

    // Where an auto-approved replacement's hold stands, in plain words.
    const holdStatus = (h: ReplacementHold): string => {
        if (h.status === "held") return t("rt_rep_held");
        if (h.status === "released") {
            return h.released_via
                ? t("rt_rep_released", { date: when(h.released_at), how: replacementViaText(t, h.released_via) })
                : t("rt_rep_released_plain", { date: when(h.released_at) });
        }
        return t("rt_rep_cancelled", { date: when(h.cancelled_at) });
    };

    // What to do now, in plain words (the box above the details).
    const nextStep = (): { title: string; body: string } => {
        const s = d.status;
        if (s === "review") return { title: t("rt_next_title_review"), body: t(d.flags.claim ? "rt_next_body_review_claim" : "rt_next_body_review") };
        if (needsLabel) return { title: t(d.label_by_hand ? "rt_next_title_label_by_hand" : "rt_next_title_label"), body: t(d.label_by_hand ? "rt_next_body_label_by_hand" : "rt_next_body_label") };
        if (s === "exchange_booked") return { title: t("rt_next_title_exchange"), body: ex ? exchangeStatus(ex) : t("rt_next_body_transit") };
        if (s === "awaiting_return") {
            const pickup = d.parcels.find((p) => p.kind === "collection" && p.collection_date && (p.status === "booked" || p.status === "pending"));
            return { title: t("rt_next_title_awaiting"), body: pickup ? t("rt_next_body_awaiting_collection", { date: day(pickup.collection_date) }) : t("rt_next_body_awaiting") };
        }
        if (s === "in_transit") return { title: t("rt_next_title_transit"), body: t("rt_next_body_transit") };
        if (s === "received") return { title: t("rt_next_title_received"), body: t(`rt_next_body_received_${d.resolution}`) };
        if (s === "refund_pending") return { title: t("rt_next_title_refund_pending"), body: t("rt_next_body_refund_pending", { amount: gbp(d.money.refund_total) }) };
        return { title: t(`rt_next_title_${s}`), body: t("rt_next_body_done") };
    };
    const next = nextStep();

    return (
        <Shell onClose={onClose} title={d.reference} pills={(
            <>
                <StatusPill status={d.status} t={t} />
                <span className="rt-pill rt-pill--plain">{problemLabel(t, d.problem)} · {resolutionLabel(t, d.resolution)}</span>
                {d.flags.claim && <span className="rt-pill rt-pill--amber">{t("rt_flag_claim")}</span>}
                {d.flags.keep_it && <span className="rt-pill rt-pill--plain">{t("rt_flag_keep")}</span>}
            </>
        )} sub={t("rt_started", { when: when(d.created_at), channel: d.channel_name })}>
            <div className={`rt-next${open ? "" : " rt-next--closed"}`} data-testid="rt-next">
                <div className="rt-next-text">
                    <strong>{next.title}</strong>
                    <span>{next.body}</span>
                </div>
                {(actions.length > 0 || more.length > 0) && (
                    <div className="rt-actions">
                        {actions}
                        {more.length > 0 && <span className="rt-actions-more" role="group" aria-label={t("rt_more_actions")}>{more}</span>}
                    </div>
                )}
            </div>
            {d.replaced_by && (
                <p className="rt-warn rt-relation" data-testid="rt-replaced-by">
                    <AlertTriangle size={14} /> <span>{t("rt_replaced_by")} {requestLink(d.replaced_by)}</span>
                </p>
            )}
            {d.money.auto_action_failed && (
                <p className="rt-warn"><AlertTriangle size={14} /> {t("rt_auto_failed", { why: d.money.auto_action_failed })}</p>
            )}
            {d.money.exchange_shortfall && (
                <p className="rt-warn" data-testid="rt-exchange-shortfall"><AlertTriangle size={14} /> {t("rt_exchange_shortfall", {
                    short: gbp(d.money.exchange_shortfall.pence),
                    credit: gbp(d.money.exchange_shortfall.accepted_credit_pence),
                    basket: gbp(d.money.exchange_shortfall.basket_pence),
                })}</p>
            )}
            {ex?.stock_short && ex.status === "held" && (
                <div className="rt-warn" data-testid="rt-exchange-stock-short">
                    <AlertTriangle size={14} />
                    <div>
                        <strong className="rt-block">{t("rt_exc_stock_short", { when: when(ex.stock_short.at) })}</strong>
                        <ul className="rt-exc-short">
                            {ex.stock_short.missing.map((m) => (
                                <li key={m.sku}>
                                    <span className="rt-mono">{m.sku}</span>: {t("rt_exc_short_line", { needed: String(m.needed), available: String(m.available) })}
                                </li>
                            ))}
                        </ul>
                        <span className="rt-block">{t("rt_exc_stock_short_help")}</span>
                    </div>
                </div>
            )}
            <div className="rt-panel-body rt-grid">
                <div className="rt-col">
                    <Section title={t("rt_items")}>
                        {d.lines.map((l) => <LineRow key={l.id} line={l} t={t} photos={photoMeta(l.photo_ids)} />)}
                        {d.order_photos.length > 0 && (
                            <div className="rt-order-photos">
                                <span className="rt-label">{t("rt_order_photos")}</span>
                                <PhotoStrip photos={photoMeta(d.order_photos.map((p) => p.photo_id))} />
                            </div>
                        )}
                    </Section>
                    {ex && (
                        <Section title={t("rt_exc_title")}>
                            <div data-testid="rt-exchange">
                                <p className="rt-exc-status">{exchangeStatus(ex)}</p>
                                {ex.test_hold && <p className="rt-muted rt-small">{t("rt_exc_test_hold")}</p>}
                                {ex.basket.map((b) => (
                                    <div key={`${b.product_id}-${b.sku}`} className="rt-line">
                                        {b.image_url ? <img className="rt-line-img" src={b.image_url} alt="" /> : <span className="rt-line-img" />}
                                        <div className="rt-line-main">
                                            <strong>{b.title}</strong>
                                            <span className="rt-mono rt-muted">
                                                {b.sku} · {t("rt_qty", { n: String(b.qty) })} · {t("rt_exc_each", { price: gbp(b.unit_price_pence) })}
                                            </span>
                                        </div>
                                        <div className="rt-line-money"><strong>{gbp(b.unit_price_pence * b.qty)}</strong></div>
                                    </div>
                                ))}
                                <dl className="rt-kv rt-kv--money rt-exc-total">
                                    <dt className="rt-strong">{t("rt_exc_total")}</dt><dd className="rt-strong">{gbp(ex.basket_value_pence)}</dd>
                                    {ex.delivery?.speed === "express" && (
                                        <>
                                            <dt>{t("rt_exc_express")}</dt>
                                            <dd data-testid="rt-exc-express">{ex.delivery.fee_pence > 0 ? gbp(ex.delivery.fee_pence) : t("rt_exc_express_fastfox")}</dd>
                                        </>
                                    )}
                                </dl>
                            </div>
                        </Section>
                    )}
                    {d.contacts && d.contacts.length > 0 && (
                        <section className="rt-sec"><ContactList contacts={d.contacts} showOrder={d.orders.length > 1} /></section>
                    )}
                    <Section title={t("rt_timeline")}>
                        <ol className="rt-timeline">
                            {d.events.map((e) => {
                                const { title, detail } = eventText(t, e);
                                return (
                                    <li key={e.id}>
                                        <span className="rt-dot" />
                                        <span>
                                            <strong>{title}</strong>
                                            <span className="rt-muted">{when(e.created_at)}{e.by ? ` · ${e.by}` : ""}</span>
                                            {detail && <span className="rt-timeline-detail">{detail}</span>}
                                        </span>
                                    </li>
                                );
                            })}
                        </ol>
                        <form className="rt-note-form" onSubmit={submitNote}>
                            <label className="rt-label" htmlFor="rt-note">{t("rt_add_note")}</label>
                            <textarea id="rt-note" className="rt-input" rows={2} maxLength={2000} value={note}
                                onChange={(e) => setNote(e.target.value)} placeholder={t("rt_note_placeholder")} disabled={!write} />
                            <div className="rt-row-end">
                                <button type="submit" className="btn btn-sm rt-btn btn-secondary" disabled={!write || !note.trim() || busy !== null}>
                                    <StickyNote size={14} /> {t("rt_save_note")}
                                </button>
                            </div>
                        </form>
                    </Section>
                </div>
                <div className="rt-col rt-col--side">
                    <Section title={t("rt_customer")}>
                        <dl className="rt-kv">
                            <dt>{t("rt_name")}</dt><dd>{d.customer.name ?? "-"}</dd>
                            <dt>{t("rt_email")}</dt><dd><a href={`mailto:${d.customer.email}`}>{d.customer.email}</a></dd>
                            {d.customer.address.length > 0 && (<><dt>{t("rt_address")}</dt><dd>{d.customer.address.join(", ")}</dd></>)}
                            {d.customer.phone && (<><dt>{t("rt_phone")}</dt><dd>{d.customer.phone}</dd></>)}
                            <dt>{t("rt_entry")}</dt>
                            <dd data-testid="rt-entry">
                                {entryText}
                                {madeInText && <span className="rt-block rt-muted rt-small" data-testid="rt-made-in-window">{madeInText}</span>}
                            </dd>
                            {d.staff_note && (<><dt>{t("rt_staff_note")}</dt><dd className="rt-pre">{d.staff_note}</dd></>)}
                            {replacesRefs.length > 0 && (
                                <><dt>{t("rt_replaces")}</dt><dd className="rt-relation" data-testid="rt-replaces">{replacesRefs.map(requestLink)}</dd></>
                            )}
                            {d.customer.return_rate_pct !== null && (<><dt>{t("rt_return_rate")}</dt><dd>{d.customer.return_rate_pct}%</dd></>)}
                            {d.customer.created_ip && (<><dt>{t("rt_ip")}</dt><dd className="rt-mono">{d.customer.created_ip}</dd></>)}
                        </dl>
                    </Section>
                    <Section title={t("rt_orders")}>
                        {d.orders.map((o) => (
                            <div key={o.order_reference} className="rt-order">
                                <strong>#{o.order_reference}</strong>
                                <span className="rt-muted">
                                    {o.delivered_at ? t("rt_delivered_on", { date: day(o.delivered_at) }) : t("rt_not_delivered")}
                                    {o.return_by ? ` · ${t("rt_return_by", { date: day(o.return_by) })}` : ""}
                                </span>
                                <span className="rt-links">
                                    {o.parts.map((p) => (
                                        <Link key={p.order_id} className="rt-chip" to={orderLink(p.order_id)}>
                                            {p.order_number ?? p.order_id}{p.warehouse ? ` · ${p.warehouse}` : ""}{p.status ? ` · ${orderStatusText(t, p.status)}` : ""}
                                            <ExternalLink size={12} />
                                        </Link>
                                    ))}
                                </span>
                                {/* The boxes it went out in, each opening the carrier's tracking page: the
                                    quickest check of a "not arrived" or "missing" claim (Jack, 2026-11-03). */}
                                {(o.parcels ?? []).length > 0 && (
                                    <span className="rt-links" data-testid="rt-outbound">
                                        {o.parcels!.map((p) => {
                                            const said = p.description && !/^delivered$/i.test(p.description.trim()) ? p.description : "";
                                            const state = p.status === "delivered"
                                                ? `${t("rt_delivered_on", { date: when(p.status_at) })}${said ? ` (${said})` : ""}`
                                                : said || p.status;
                                            const text = <><Truck size={12} /> {p.carrier ?? ""} <span className="rt-mono">{p.tracking_number}</span> · {state}</>;
                                            return p.tracking_url ? (
                                                <a key={p.tracking_number} className="rt-chip" href={p.tracking_url} target="_blank" rel="noopener noreferrer"
                                                    title={t("rt_track_title", { carrier: p.carrier ?? "" })}>
                                                    {text}
                                                    <ExternalLink size={12} />
                                                </a>
                                            ) : <span key={p.tracking_number} className="rt-chip">{text}</span>;
                                        })}
                                    </span>
                                )}
                            </div>
                        ))}
                        {(d.replacement_orders.length > 0 || hold) && (
                            <div className="rt-order">
                                <span className="rt-label">{t("rt_replacement_orders")}</span>
                                {hold && <span className="rt-exc-status" data-testid="rt-replacement-hold">{holdStatus(hold)}</span>}
                                <span className="rt-links">
                                    {d.replacement_orders.map((p) => (
                                        <Link key={p.order_id} className="rt-chip" to={orderLink(p.order_id)}>
                                            {p.order_number ?? p.order_id}{p.status ? ` · ${orderStatusText(t, p.status)}` : ""}{p.hold_reason && p.status !== "on_hold" ? ` · ${t("rt_on_hold")}` : ""}
                                            <ExternalLink size={12} />
                                        </Link>
                                    ))}
                                </span>
                            </div>
                        )}
                        {d.exchange_orders.length > 0 && (
                            <div className="rt-order">
                                <span className="rt-label">{t("rt_exchange_orders")}</span>
                                <span className="rt-links">
                                    {d.exchange_orders.map((p) => (
                                        <Link key={p.order_id} className="rt-chip" to={orderLink(p.order_id)}>
                                            {p.order_number ?? p.order_id}{p.status ? ` · ${orderStatusText(t, p.status)}` : ""}
                                            <ExternalLink size={12} />
                                        </Link>
                                    ))}
                                </span>
                            </div>
                        )}
                    </Section>
                    <Section title={t("rt_money")}>
                        {/* What comes off and goes on, down to the one figure that matters: the refund due now.
                            What the customer paid us is a separate line, never part of the refund. */}
                        <dl className="rt-kv rt-kv--money" data-testid="rt-money">
                            <dt>{t("rt_items_value")}</dt><dd>{gbp(q.items_value)}</dd>
                            <dt className="rt-kv-sub">{t("rt_vat_incl")}</dt><dd className="rt-kv-sub">{gbp(q.vat)}</dd>
                            {q.delivery_refund > 0 && (<><dt>{t("rt_delivery_refund")}</dt><dd>{gbp(q.delivery_refund)}</dd></>)}
                            {d.flags.needs_return && (<><dt>{t("rt_postage")}</dt><dd>{q.postage ? `-${gbp(q.postage)}` : t("rt_postage_free")}</dd></>)}
                            {ex && (<><dt>{t("rt_new_items_value")}</dt><dd>{gbp(ex.basket_value_pence)}</dd></>)}
                            {q.payable > 0 && (<><dt>{t("rt_customer_paid")}</dt><dd>{gbp(q.payable)}</dd></>)}
                            <dt className="rt-strong rt-kv-total">{t("rt_refund_due")}</dt><dd className="rt-strong rt-kv-total">{gbp(d.money.refund_total)}</dd>
                        </dl>
                        {q.refund_total !== d.money.refund_total && <p className="rt-muted rt-small">{t("rt_quoted_at_start", { amount: gbp(q.refund_total) })}</p>}
                        {d.money.service_id && (
                            <p className="rt-muted rt-small">{t("rt_sent_back_by", { service: statusWord(t, "rt_service_name_", d.money.service_id), labels: String(d.money.labels ?? 1) })}</p>
                        )}
                    </Section>
                    <Section title={t("rt_parcels")}>
                        {/* A manual return sent to a warehouse of staff's choosing (2026-11-04), and one whose label is added by hand. */}
                        {d.return_to && (
                            <p className="rt-muted rt-small rt-new-wrap" data-testid="rt-return-to">
                                {t("rt_goes_back_to", {
                                    warehouse: d.return_to.warehouse,
                                    address: [d.return_to.address?.company, d.return_to.address?.line_1, d.return_to.address?.city, d.return_to.address?.postcode, d.return_to.address?.country].filter(Boolean).join(", "),
                                })}
                            </p>
                        )}
                        {d.label_by_hand && d.parcels.length === 0 && <p className="rt-warn" data-testid="rt-label-by-hand"><AlertTriangle size={14} /> {t("rt_label_by_hand_note")}</p>}
                        {d.parcels.length === 0 && !d.label_by_hand && <p className="rt-muted rt-small">{d.flags.needs_return ? t("rt_no_parcels") : t("rt_nothing_back")}</p>}
                        {d.parcels.map((p) => (
                            <div key={p.parcel_no} className="rt-parcel">
                                <span>
                                    <strong>{t("rt_parcel_n", { n: String(p.parcel_no) })}</strong> · {carrierName(p.carrier_code)} · {statusWord(t, "rt_parcel_status_", p.status)}
                                    {p.tracking_number && (p.tracking_url
                                        ? <a className="rt-mono rt-block" href={p.tracking_url} target="_blank" rel="noopener noreferrer"
                                            title={t("rt_track_title", { carrier: carrierName(p.carrier_code) })}>{p.tracking_number} <ExternalLink size={12} /></a>
                                        : <span className="rt-mono rt-block">{p.tracking_number}</span>)}
                                    {p.booking_error && <span className="rt-error rt-block">{p.booking_error}</span>}
                                </span>
                                <span className="rt-parcel-actions">
                                    {p.has_label_pdf && (
                                        <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={() => openFile(labelPdfPath(reference, p.parcel_no))}>
                                            <FileText size={14} /> {t("rt_label_pdf")}
                                        </button>
                                    )}
                                    {p.status === "booked" && (
                                        <button type="button" className="btn btn-sm rt-btn btn-ghost" disabled={!write || busy !== null}
                                            onClick={() => act(`void-${p.parcel_no}`, () => voidLabel(reference, p.parcel_no), t("rt_label_voided"))}>
                                            <X size={14} /> {t("rt_void")}
                                        </button>
                                    )}
                                </span>
                            </div>
                        ))}
                    </Section>
                    {d.payments.length > 0 && (
                        <Section title={t("rt_payments")}>
                            {/* What the customer paid us is not what we owe them for the return: said first (Jack, 2026-11-04). */}
                            <p className="rt-muted rt-small rt-pay-hint" data-testid="rt-payments-hint">{t("rt_payments_hint")}</p>
                            {/* Each payment with its sales invoice, then each refund of it with its credit invoice (Jack, 2026-11-03). */}
                            {d.payments.map((p) => (
                                <Fragment key={p.id}>
                                    <div className="rt-parcel">
                                        <span>
                                            <strong>{t("rt_payment_line", { amount: gbp(p.amount_pence) })}</strong> · {statusWord(t, "rt_pay_status_", p.status)}
                                            {paidWith(p, t) && <span className="rt-block rt-muted">{paidWith(p, t)}</span>}
                                            {p.payment_id && <span className="rt-block rt-mono">{p.payment_id}</span>}
                                        </span>
                                        {p.invoice_number && (
                                            <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={() => openFile(invoicePdfPath(p.invoice_number!))}>
                                                <FileText size={14} /> {t("rt_sales_invoice", { number: p.invoice_number })}
                                            </button>
                                        )}
                                    </div>
                                    {(p.refunds ?? []).map((r) => (
                                        <div key={`refund-${r.id}`} className="rt-parcel">
                                            <span>
                                                <strong>{t("rt_refund_line", { amount: gbp(r.amount_pence) })}</strong> · {statusWord(t, "rt_refund_status_", r.status)}
                                                <span className="rt-block rt-muted">{[when(r.settled_at ?? r.created_at), r.requested_by_name].filter(Boolean).join(" · ")}</span>
                                                {r.reason && <span className="rt-block">{r.reason}</span>}
                                                {r.error && <span className="rt-block rt-error">{r.error}</span>}
                                            </span>
                                            {r.credit_note_number && (
                                                <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={() => openFile(creditNotePdfPath(r.credit_note_number!))}>
                                                    <FileText size={14} /> {t("rt_credit_invoice", { number: r.credit_note_number })}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </Fragment>
                            ))}
                            {/* Quiet and at the bottom: a deliberate step, with its own warnings (RefundPaymentDialog). */}
                            {capturedPayment && paymentLeft > 0 && (
                                <div className="rt-pay-refund">
                                    <button type="button" className="rt-link-btn rt-link-btn--danger" disabled={!write || busy !== null}
                                        title={!write ? t("read_only_mode") : undefined} onClick={() => setDialog("refund_payment")}>
                                        <CreditCard size={14} /> {t("rt_refund_payment")}
                                    </button>
                                </div>
                            )}
                        </Section>
                    )}
                </div>
            </div>

            {dialog === "approve" && (
                <ApproveDialog d={d} t={t} busy={busy !== null} onClose={() => setDialog(null)}
                    onSubmit={async (resolution, message) => {
                        const r = await act("approve", () => approveReturn(reference, { resolution, message: message || undefined }), t("rt_approved"));
                        if (r?.ok) setDialog(null);
                    }} />
            )}
            {dialog === "decline" && (
                <MessageDialog t={t} title={t("rt_decline_title")} intro={t("rt_decline_intro")} label={t("rt_message_customer")} required
                    submitLabel={t("rt_decline")} busy={busy !== null} onClose={() => setDialog(null)}
                    onSubmit={async (message) => {
                        const r = await act("decline", () => declineReturn(reference, { message }), t("rt_declined"));
                        if (r?.ok) setDialog(null);
                    }} />
            )}
            {dialog === "cancel" && (
                <CancelDialog t={t} busy={busy !== null} onClose={() => setDialog(null)}
                    onSubmit={async (reason, message) => {
                        const r = await act("cancel", () => cancelReturn(reference, { reason: reason || undefined, message: message || undefined }), t("rt_cancelled"));
                        if (r?.ok) {
                            setDialog(null);
                            // The BE voids the unused labels itself (APC per consignment); say what happened.
                            const voided = r.data?.labels_voided;
                            if (Array.isArray(voided)) {
                                const ok = voided.filter((v) => v.outcome === "voided" || v.outcome === "already").map((v) => v.waybill);
                                const notOk = voided.filter((v) => v.outcome === "manifested" || v.outcome === "failed").map((v) => v.waybill);
                                if (ok.length) toast.success(t("rt_labels_voided", { waybills: ok.join(", ") }));
                                if (notOk.length) toast.warn(t("rt_labels_void_failed", { waybills: notOk.join(", ") }), { autoClose: false });
                            } else {
                                const toVoid = r.data?.parcels_to_void ?? [];
                                if (toVoid.length > 0) {
                                    toast.warn(t("rt_void_reminder", { parcels: toVoid.map((p) => p.tracking_number ?? `#${p.parcel_no}`).join(", ") }), { autoClose: false });
                                }
                            }
                        }
                    }} />
            )}
            {dialog === "complete" && (
                <CompleteDialog d={d} t={t} busy={busy !== null} onClose={() => setDialog(null)}
                    onSubmit={async (lines, message, chargePostage) => {
                        const r = await act("complete", () => completeReturn(reference, { lines, message: message || undefined, charge_postage: chargePostage }), t("rt_completed"));
                        if (r?.ok) setDialog(null);
                    }} />
            )}
            {dialog === "manual" && (
                <ManualLabelDialog t={t} busy={busy !== null} onClose={() => setDialog(null)}
                    onSubmit={async (form) => {
                        const r = await act("manual", () => addManualLabel(reference, form), t("rt_label_added"));
                        if (r?.ok || r?.notAvailable) setDialog(null);
                    }} />
            )}
            {dialog === "refund_payment" && capturedPayment && (
                <RefundPaymentDialog d={d} t={t} left={paymentLeft} busy={busy !== null} onClose={() => setDialog(null)}
                    onSubmit={async (reason, confirmPence) => {
                        const r = await act("refund_payment", () => refundPayment(reference, { reason, confirm_pence: confirmPence }), t("rt_payment_refunded"));
                        if (r?.ok || r?.notAvailable) setDialog(null);
                    }} />
            )}
            {dialog === "release" && (
                <ConfirmDialog t={t} title={t("rt_release_exchange")}
                    body={ex?.test_hold ? `${t("rt_release_exchange_confirm")} ${t("rt_exc_test_hold")}` : t("rt_release_exchange_confirm")}
                    confirmLabel={t("rt_release_exchange")} busy={busy !== null} onClose={() => setDialog(null)}
                    onConfirm={async () => {
                        const r = await act("release", () => releaseExchange(reference), t("rt_exchange_released"));
                        // stock_short: close so the warning on the request shows.
                        if (r?.ok || r?.notAvailable || r?.code === "stock_short") setDialog(null);
                    }} />
            )}
            {dialog === "release_replacement" && (
                <ConfirmDialog t={t} title={t("rt_release_replacement")} body={t("rt_release_replacement_confirm")}
                    confirmLabel={t("rt_release_replacement")} busy={busy !== null} onClose={() => setDialog(null)}
                    onConfirm={async () => {
                        // The exchange release endpoint also releases a held replacement.
                        const r = await act("release_replacement", () => releaseExchange(reference), t("rt_replacement_released"));
                        if (r?.ok || r?.notAvailable) setDialog(null);
                    }} />
            )}
        </Shell>
    );
};

// ---- pieces -----------------------------------------------------------------------------

const Shell = ({ title, sub, pills, onClose, children }: { title: string; sub?: string; pills?: ReactNode; onClose: () => void; children: ReactNode }) => {
    const { t } = useTranslation();
    return (
        <div className="rt-overlay" onClick={onClose}>
            <aside className="rt-panel" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
                {/* A div, not <header>: header.css styles every <header> as the app bar. */}
                <div className="rt-panel-head">
                    <div className="rt-panel-title">
                        <h2>{title}</h2>
                        {sub && <p className="rt-muted">{sub}</p>}
                        {pills && <div className="rt-pills">{pills}</div>}
                    </div>
                    <button type="button" className="rt-icon-btn" onClick={onClose} aria-label={t("close")}><X size={18} /></button>
                </div>
                {children}
            </aside>
        </div>
    );
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
    <section className="rt-sec">
        <h3 className="rt-h3">{title}</h3>
        {children}
    </section>
);

const LineRow = ({ line: l, t, photos }: { line: ReturnLine; t: (k: string, p?: Record<string, string>) => string; photos: { photo_id: string; mime_type?: string }[] }) => (
    <div className="rt-line">
        {l.image_url ? <img className="rt-line-img" src={l.image_url} alt="" /> : <span className="rt-line-img" />}
        <div className="rt-line-main">
            <strong>{l.title}</strong>
            <span className="rt-mono rt-muted">{l.sku} · {t("rt_qty", { n: String(l.qty) })}</span>
            <span>
                {l.reason_label}{" "}
                <span className={`rt-pill rt-pill--${l.fault === "ours" ? "blue" : "grey"}`}>{t(l.fault === "ours" ? "rt_fault_ours" : "rt_fault_customer")}</span>
            </span>
            {l.detail && <q className="rt-quote">{l.detail}</q>}
            <span className="rt-small rt-muted">
                {[
                    l.replace_qty ? t("rt_line_replaced", { n: String(l.replace_qty) }) : null,
                    l.pended_qty ? t("rt_line_pended", { n: String(l.pended_qty) }) : null,
                    l.check ? t("rt_line_checked", { a: String(l.check.accepted), r: String(l.check.rejected) }) : null,
                ].filter(Boolean).join(" · ")}
            </span>
            <PhotoStrip photos={photos} />
        </div>
        <div className="rt-line-money">
            <strong>{gbp(l.value_pence)}</strong>
            {l.delivery_refund_pence > 0 && <span className="rt-small rt-muted">+{gbp(l.delivery_refund_pence)} {t("rt_delivery_short")}</span>}
        </div>
    </div>
);

type TT = (k: string, p?: Record<string, string>) => string;

const Modal = ({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) => (
    <div className="rt-modal-overlay" onClick={onClose}>
        <div className={`rt-modal${wide ? " rt-modal--wide" : ""}`} role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
            <div className="rt-modal-head">
                <h3>{title}</h3>
                <button type="button" className="rt-icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
            </div>
            {children}
        </div>
    </div>
);

const ApproveDialog = ({ d, t, busy, onClose, onSubmit }: {
    d: ReturnDetail; t: TT; busy: boolean; onClose: () => void; onSubmit: (resolution: "replacement" | "refund", message: string) => void;
}) => {
    const replacementOk = d.problem !== "return" && d.lines.every((l) => l.fault === "ours");
    const [resolution, setResolution] = useState<"replacement" | "refund">(d.resolution === "replacement" && replacementOk ? "replacement" : "refund");
    const [message, setMessage] = useState("");
    return (
        <Modal title={t("rt_approve_title")} onClose={onClose}>
            <form className="rt-modal-body" onSubmit={(e) => { e.preventDefault(); onSubmit(resolution, message.trim()); }}>
                <p className="rt-muted">{t("rt_approve_intro", { asked: resolutionLabel(t, d.resolution) })}</p>
                <fieldset className="rt-choice">
                    <label className={!replacementOk ? "rt-disabled" : undefined}>
                        <input type="radio" name="res" value="replacement" checked={resolution === "replacement"} disabled={!replacementOk} onChange={() => setResolution("replacement")} />
                        <span><strong>{t("rt_res_replacement")}</strong><small>{replacementOk ? t("rt_approve_replacement_hint") : t("rt_approve_replacement_no")}</small></span>
                    </label>
                    <label>
                        <input type="radio" name="res" value="refund" checked={resolution === "refund"} onChange={() => setResolution("refund")} />
                        <span><strong>{t("rt_res_refund")}</strong><small>{d.flags.needs_return ? t("rt_approve_refund_back") : t("rt_approve_refund_now")}</small></span>
                    </label>
                </fieldset>
                <label className="rt-label" htmlFor="rt-approve-msg">{t("rt_message_optional")}</label>
                <textarea id="rt-approve-msg" className="rt-input" rows={3} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} />
                <div className="rt-modal-foot">
                    <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={onClose}>{t("cancel")}</button>
                    <button type="submit" className="btn btn-sm rt-btn btn-primary" disabled={busy}><Check size={14} /> {t("rt_approve")}</button>
                </div>
            </form>
        </Modal>
    );
};

const MessageDialog = ({ t, title, intro, label, required, submitLabel, busy, onClose, onSubmit }: {
    t: TT; title: string; intro: string; label: string; required?: boolean; submitLabel: string; busy: boolean; onClose: () => void; onSubmit: (message: string) => void;
}) => {
    const [message, setMessage] = useState("");
    const [touched, setTouched] = useState(false);
    const missing = required && !message.trim();
    return (
        <Modal title={title} onClose={onClose}>
            <form className="rt-modal-body" onSubmit={(e) => { e.preventDefault(); setTouched(true); if (!missing) onSubmit(message.trim()); }}>
                <p className="rt-muted">{intro}</p>
                <label className="rt-label" htmlFor="rt-msg">{label}</label>
                <textarea id="rt-msg" className={`rt-input${touched && missing ? " rt-input--error" : ""}`} rows={4} maxLength={2000}
                    value={message} onChange={(e) => setMessage(e.target.value)} aria-invalid={touched && missing ? true : undefined} />
                {touched && missing && <p className="rt-field-error">{t("rt_message_required")}</p>}
                <div className="rt-modal-foot">
                    <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={onClose}>{t("rt_keep")}</button>
                    <button type="submit" className="btn btn-sm rt-btn btn-primary" disabled={busy}>{submitLabel}</button>
                </div>
            </form>
        </Modal>
    );
};

// Cancel: the customer is always emailed (return_cancelled). The reason stays
// with staff; the message, if any, is what the customer reads.
const CancelDialog = ({ t, busy, onClose, onSubmit }: {
    t: TT; busy: boolean; onClose: () => void; onSubmit: (reason: string, message: string) => void;
}) => {
    const [reason, setReason] = useState("");
    const [message, setMessage] = useState("");
    return (
        <Modal title={t("rt_cancel_title")} onClose={onClose}>
            <form className="rt-modal-body" onSubmit={(e) => { e.preventDefault(); onSubmit(reason.trim(), message.trim()); }}>
                <p className="rt-muted">{t("rt_cancel_intro")}</p>
                <label className="rt-label" htmlFor="rt-cancel-reason">{t("rt_cancel_reason")}</label>
                <textarea id="rt-cancel-reason" className="rt-input" rows={2} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} />
                <label className="rt-label" htmlFor="rt-cancel-message">{t("rt_cancel_message")}</label>
                <textarea id="rt-cancel-message" className="rt-input" rows={3} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)}
                    aria-describedby="rt-cancel-message-hint" />
                <p id="rt-cancel-message-hint" className="rt-muted rt-small">{t("rt_cancel_message_hint")}</p>
                <div className="rt-modal-foot">
                    <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={onClose}>{t("rt_keep")}</button>
                    <button type="submit" className="btn btn-sm rt-btn btn-primary" disabled={busy}>{t("rt_cancel")}</button>
                </div>
            </form>
        </Modal>
    );
};

const CompleteDialog = ({ d, t, busy, onClose, onSubmit }: {
    d: ReturnDetail; t: TT; busy: boolean; onClose: () => void;
    /** chargePostage is undefined when the request can't be charged postage (no d.postage_charge). */
    onSubmit: (lines: { return_item_id: number; accept_qty: number }[], message: string, chargePostage: boolean | undefined) => void;
}) => {
    const checkable = (l: ReturnLine) => Math.max(0, l.qty - l.pended_qty);
    const [accept, setAccept] = useState<Record<number, number>>(() => Object.fromEntries(d.lines.map((l) => [l.id, checkable(l)])));
    const [message, setMessage] = useState("");
    const [touched, setTouched] = useState(false);
    // Not damaged, faulty or wrong after all: charge the return postage, and tell the customer why.
    const postage = d.postage_charge ?? null;
    const [chargePostage, setChargePostage] = useState(false);
    const charging = !!postage && chargePostage;
    const rejected = d.lines.reduce((n, l) => n + (checkable(l) - (accept[l.id] ?? 0)), 0);
    const missing = (rejected > 0 || charging) && !message.trim();
    return (
        <Modal title={t("rt_complete_title")} onClose={onClose} wide>
            <form className="rt-modal-body" onSubmit={(e) => {
                e.preventDefault(); setTouched(true);
                if (!missing) onSubmit(d.lines.map((l) => ({ return_item_id: l.id, accept_qty: accept[l.id] ?? 0 })), message.trim(), postage ? chargePostage : undefined);
            }}>
                <p className="rt-muted">{d.resolution === "refund" ? t("rt_complete_intro_refund") : t("rt_complete_intro_other")}</p>
                <div className="rt-check-table" role="table">
                    <div className="rt-check-row rt-check-row--head" role="row">
                        <span role="columnheader">{t("rt_item")}</span>
                        <span role="columnheader">{t("rt_came_back")}</span>
                        <span role="columnheader">{t("rt_accept")}</span>
                    </div>
                    {d.lines.map((l) => (
                        <div key={l.id} className="rt-check-row" role="row">
                            <span role="cell"><strong>{l.title}</strong><span className="rt-mono rt-muted rt-block">{l.sku}</span></span>
                            <span role="cell">{checkable(l)}</span>
                            <span role="cell">
                                <input type="number" className="rt-input rt-input--qty" min={0} max={checkable(l)} step={1}
                                    aria-label={t("rt_accept_qty_for", { item: l.title })}
                                    value={accept[l.id] ?? 0}
                                    onChange={(e) => {
                                        const n = Math.max(0, Math.min(checkable(l), Math.floor(Number(e.target.value) || 0)));
                                        setAccept((a) => ({ ...a, [l.id]: n }));
                                    }} />
                            </span>
                        </div>
                    ))}
                </div>
                {rejected > 0 && <p className="rt-warn"><AlertTriangle size={14} /> {t("rt_rejecting", { n: String(rejected) })}</p>}
                {postage && (
                    <label className="rs-check rt-charge-postage">
                        <input type="checkbox" checked={chargePostage} onChange={(e) => setChargePostage(e.target.checked)} />
                        <span><strong>{t("rt_charge_postage", { amount: gbp(postage.pence) })}</strong><small>{t("rt_charge_postage_hint")}</small></span>
                    </label>
                )}
                <label className="rt-label" htmlFor="rt-complete-msg">{rejected > 0 || charging ? t("rt_message_customer") : t("rt_message_optional")}</label>
                <textarea id="rt-complete-msg" className={`rt-input${touched && missing ? " rt-input--error" : ""}`} rows={3} maxLength={2000}
                    placeholder={charging ? t("rt_charge_postage_placeholder") : undefined}
                    value={message} onChange={(e) => setMessage(e.target.value)} />
                {touched && missing && <p className="rt-field-error">{t(charging ? "rt_charge_postage_message_required" : "rt_message_required_reject")}</p>}
                <div className="rt-modal-foot">
                    <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={onClose}>{t("cancel")}</button>
                    <button type="submit" className="btn btn-sm rt-btn btn-primary" disabled={busy}><ClipboardCheck size={14} /> {t("rt_complete")}</button>
                </div>
            </form>
        </Modal>
    );
};

const CARRIERS = ["royalmail", "inpost", "apc", "other"] as const;

const ManualLabelDialog = ({ t, busy, onClose, onSubmit }: { t: TT; busy: boolean; onClose: () => void; onSubmit: (form: FormData) => void }) => {
    const [carrier, setCarrier] = useState<string>("royalmail");
    const [tracking, setTracking] = useState("");
    const [code, setCode] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [touched, setTouched] = useState(false);
    // Tracking numbers are case-sensitive: sent exactly as typed (trimmed).
    const missing = !tracking.trim();
    // InPost: the drop-off code the customer types at the locker (4-32 letters or digits; optional).
    const codeText = code.replace(/\s+/g, "");
    const badCode = carrier === "inpost" && codeText !== "" && !/^[A-Za-z0-9]{4,32}$/.test(codeText);
    return (
        <Modal title={t("rt_manual_label")} onClose={onClose}>
            <form className="rt-modal-body" onSubmit={(e) => {
                e.preventDefault(); setTouched(true);
                if (missing || badCode) return;
                const form = new FormData();
                form.append("carrier", carrier);
                form.append("tracking_number", tracking.trim());
                if (carrier === "inpost" && codeText) form.append("drop_off_code", codeText);
                if (file) form.append("file", file);
                onSubmit(form);
            }}>
                <p className="rt-muted">{t("rt_manual_intro")}</p>
                <label className="rt-label" htmlFor="rt-carrier">{t("rt_carrier")}</label>
                <select id="rt-carrier" className="rt-input" value={carrier} onChange={(e) => setCarrier(e.target.value)}>
                    {CARRIERS.map((c) => <option key={c} value={c}>{t(`rt_carrier_${c}`)}</option>)}
                </select>
                <label className="rt-label" htmlFor="rt-tracking">{t("rt_tracking_number")}</label>
                <input id="rt-tracking" className={`rt-input rt-mono${touched && missing ? " rt-input--error" : ""}`} value={tracking}
                    autoComplete="off" spellCheck={false} onChange={(e) => setTracking(e.target.value)} />
                {touched && missing && <p className="rt-field-error">{t("rt_tracking_required")}</p>}
                {carrier === "inpost" && (
                    <>
                        <label className="rt-label" htmlFor="rt-drop-off-code">{t("rt_drop_off_code")}</label>
                        <input id="rt-drop-off-code" className={`rt-input rt-mono${badCode ? " rt-input--error" : ""}`} value={code}
                            autoComplete="off" spellCheck={false} onChange={(e) => setCode(e.target.value)} />
                        <small className="rt-muted">{t("rt_drop_off_code_hint")}</small>
                        {badCode && <p className="rt-field-error">{t("rt_drop_off_code_invalid")}</p>}
                    </>
                )}
                <label className="rt-label" htmlFor="rt-label-file">{t("rt_label_file")}</label>
                <input id="rt-label-file" type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                <div className="rt-modal-foot">
                    <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={onClose}>{t("cancel")}</button>
                    <button type="submit" className="btn btn-sm rt-btn btn-primary" disabled={busy}><Upload size={14} /> {t("rt_save_label")}</button>
                </div>
            </form>
        </Modal>
    );
};

const ConfirmDialog = ({ t, title, body, confirmLabel, busy, onClose, onConfirm }: {
    t: TT; title: string; body: string; confirmLabel: string; busy: boolean; onClose: () => void; onConfirm: () => void;
}) => (
    <Modal title={title} onClose={onClose}>
        <div className="rt-modal-body">
            <p>{body}</p>
            <div className="rt-modal-foot">
                <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={onClose}>{t("rt_keep")}</button>
                <button type="button" className="btn btn-sm rt-btn btn-primary" disabled={busy} onClick={onConfirm}>{confirmLabel}</button>
            </div>
        </div>
    </Modal>
);

type RefundWhy = "cancelled" | "mistake" | "other";
const REFUND_WHYS: RefundWhy[] = ["cancelled", "mistake", "other"];

/**
 * Refunding what the customer paid on the returns site (an exchange's top-up). It is never the
 * refund for the items sent back, and staff took it for that when a return came in (Jack,
 * 2026-11-04), so the dialog says so first, asks why, and the amount must be typed to confirm.
 * `left` is what is still theirs to get back (leftToRefund); the BE refuses the refund
 * (amount_changed) when that is no longer so.
 */
export const RefundPaymentDialog = ({ d, t, left, busy, onClose, onSubmit }: {
    d: ReturnDetail; t: TT; left: number; busy: boolean; onClose: () => void; onSubmit: (reason: string, confirmPence: number) => void;
}) => {
    const [why, setWhy] = useState<RefundWhy | null>(null);
    const [other, setOther] = useState("");
    const [typed, setTyped] = useState("");
    const [touched, setTouched] = useState(false);
    const amount = (left / 100).toFixed(2);
    // "4.37", "£4.37" or "4,37" (a Polish keyboard) all confirm £4.37.
    const typedOk = typed.trim().replace(/^£\s*/, "").replace(",", ".") === amount;
    const otherText = other.trim();
    const reasonOk = why !== null && (why !== "other" || otherText.length >= 3);
    const reason = why === "cancelled" ? t("rt_refund_pay_reason_cancelled_text") : why === "mistake" ? t("rt_refund_pay_reason_mistake_text") : otherText;
    const ready = left > 0 && reasonOk && typedOk;
    // The button waits until all is right, so mistakes show as they are typed.
    const typeErr = (touched || typed.trim().length >= amount.length) && !typedOk;
    const otherErr = (touched || otherText !== "") && otherText.length < 3;
    // The new items have already gone to the warehouse: refunding means they go out unpaid for.
    const released = d.exchange?.status === "released";
    const orders = d.exchange_orders.map((o) => o.order_number ?? o.order_id).join(", ");
    return (
        <Modal title={t("rt_refund_pay_title")} onClose={onClose}>
            <form className="rt-modal-body" onSubmit={(e) => { e.preventDefault(); setTouched(true); if (ready && !busy) onSubmit(reason, left); }}>
                <div className="rt-danger" data-testid="rt-refund-pay-warning">
                    <AlertTriangle size={16} />
                    <div>
                        <strong className="rt-block">{t("rt_refund_pay_not_items")}</strong>
                        {t("rt_refund_pay_what", { amount: gbp(left) })}
                    </div>
                </div>
                {released && <p className="rt-warn" data-testid="rt-refund-pay-released"><AlertTriangle size={14} /> {t("rt_refund_pay_released", { orders })}</p>}
                {left <= 0 ? <p className="rt-muted">{t("rt_refund_pay_nothing_left")}</p> : (
                    <>
                        <fieldset className="rt-choice rt-choice--tight">
                            <legend className="rt-label">{t("rt_refund_pay_when")}</legend>
                            {REFUND_WHYS.map((k) => (
                                <label key={k}>
                                    <input type="radio" name="rt-refund-why" value={k} checked={why === k} onChange={() => setWhy(k)} />
                                    <span>{t(`rt_refund_pay_reason_${k}`)}</span>
                                </label>
                            ))}
                        </fieldset>
                        {why === "other" && (
                            <>
                                <label className="rt-label" htmlFor="rt-refund-other">{t("rt_refund_pay_reason_other_label")}</label>
                                <input id="rt-refund-other" className={`rt-input${otherErr ? " rt-input--error" : ""}`} maxLength={200}
                                    autoComplete="off" value={other} onChange={(e) => setOther(e.target.value)} />
                                {otherErr && <p className="rt-field-error">{t("rt_refund_pay_reason_short")}</p>}
                            </>
                        )}
                        <label className="rt-label" htmlFor="rt-refund-type">{t("rt_refund_pay_type", { amount })}</label>
                        <input id="rt-refund-type" className={`rt-input rt-input--amount${typeErr ? " rt-input--error" : ""}`} inputMode="decimal"
                            autoComplete="off" spellCheck={false} value={typed} onChange={(e) => setTyped(e.target.value)} aria-invalid={typeErr ? true : undefined} />
                        {typeErr && <p className="rt-field-error">{t("rt_refund_pay_type_mismatch", { amount })}</p>}
                    </>
                )}
                <div className="rt-modal-foot">
                    <button type="button" className="btn btn-sm rt-btn btn-secondary" onClick={onClose}>{t("rt_refund_pay_keep")}</button>
                    {left > 0 && (
                        <button type="submit" className="btn btn-sm rt-btn btn-danger" disabled={busy || !ready}>
                            <CreditCard size={14} /> {t("rt_refund_pay_go", { amount: gbp(left) })}
                        </button>
                    )}
                </div>
            </form>
        </Modal>
    );
};

export default ReturnDetailPanel;
